import type { AppLanguage } from '@/i18n/translations';
import {
  collectSection1FactKeys,
  isDuplicateFact,
  rememberFact,
  singleParcelDuplicatesScalars,
} from '@/lib/api/planning-info-dedupe';
import {
  getPlanningReportBundle,
  localizePlanningView,
} from '@/lib/planning-report-i18n';
import { pdfPageUrl, isOfficialTakanonPdfUrl } from '@/lib/planning-extraction';
import { displayTitleForTakanonSection } from '@/lib/takanon-section-title';
import type { PlanningField, PlanningSection, PlanningSectionsJson } from './assessment';

type ApiReportMeta = {
  report_date?: string;
  hebrew_date?: string;
  request_type?: string;
  request_subject?: string;
};

type ApiParcelRow = { gush: number; helka: number; area_sqm: string };

type ApiSection1 = {
  title: string;
  settlement?: string;
  gush?: number;
  helka?: number;
  area_sqm?: string;
  parcels?: ApiParcelRow[];
  related_plans?: { columns: string[]; rows: Record<string, string>[] };
  footnotes?: string[];
  rows?: { label: string; value: string }[];
};

type ApiSection2Table = {
  subtitle?: string;
  columns: string[];
  rows: Record<string, string>[];
};

type ApiSection2 = {
  title: string;
  tables?: ApiSection2Table[];
  columns?: string[];
  rows?: Record<string, string>[];
};

type ApiTopicGroup = {
  category: string;
  items: { plan_ref?: string; topic: string; detail: string }[];
};

type ApiSection3 = {
  title: string;
  lot_summary?: { label: string; value: string }[];
  table_a?: { columns: string[]; rows: Record<string, string>[] };
  table_b?: { columns: string[]; rows: Record<string, string>[] };
  topic_groups?: ApiTopicGroup[];
  topics?: { label: string; value: string }[];
  rows?: { label: string; value: string }[];
  extracted_fields?: ApiExtractedField[];
};

type ApiOfficialContact = {
  role?: string;
  name?: string;
  phone?: string;
  fax?: string;
  address?: string;
};

type ApiSection4 = {
  title: string;
  notes?: string[];
  disclaimer?: string;
  editor?: string;
  approved_by?: string;
  editor_contact?: ApiOfficialContact;
  approver_contact?: ApiOfficialContact;
};

export type ApiPlanningDocument = {
  type: string;
  title: string;
  url: string;
  plan_ref?: string;
};

export type ApiExtractedField = {
  key: string;
  label_he: string;
  value: string;
  page?: number;
  confidence: number;
  quote?: string;
  verified?: boolean;
  verification_error?: string;
};

export type ApiExtractedSection = {
  section_key: string;
  title: string;
  fields: ApiExtractedField[];
};

export type ApiTakanonExtractionMeta = {
  status: string;
  plan_ref?: string;
  pdf_url?: string;
  overall_confidence?: number;
  extraction_mode?: 'scanned_pdf' | 'text_layer' | 'ocr';
};

export type ApiPlanningSections = {
  report_meta?: ApiReportMeta;
  section_1?: ApiSection1;
  section_2?: ApiSection2;
  section_3?: ApiSection3;
  section_4?: ApiSection4;
  documents?: ApiPlanningDocument[];
  extracted_sections?: ApiExtractedSection[];
  takanon_extraction?: ApiTakanonExtractionMeta;
};

export function parsePlanningExtras(
  sectionsJson: PlanningSectionsJson | ApiPlanningSections,
): {
  documents: ApiPlanningDocument[];
  extractedSections: ApiExtractedSection[];
  takanonExtraction?: ApiTakanonExtractionMeta;
} {
  const api = sectionsJson as ApiPlanningSections;
  return {
    documents: api.documents ?? [],
    extractedSections: api.extracted_sections ?? [],
    takanonExtraction: api.takanon_extraction,
  };
}

/** True when Tabanow / extraction resolved a תקנון PDF URL. */
export function hasTakanonDocument(
  extras: ReturnType<typeof parsePlanningExtras>,
): boolean {
  if (extras.takanonExtraction?.pdf_url?.trim()) return true;
  return extras.documents.some(
    (doc) => doc.type === 'takanon' && Boolean(doc.url?.trim()),
  );
}

export function hasVerifiedTakanonExtraction(
  extras: ReturnType<typeof parsePlanningExtras>,
): boolean {
  return extras.extractedSections.some((section) =>
    section.fields.some((field) => field.verified === true),
  );
}

export type PlanningReportMetaUi = ApiReportMeta;

function sanitizePlanningCell(value: string): string {
  return value.replace(/\s*\^\s*/g, '. ').replace(/\s+/g, ' ').trim();
}

function tableRowsToMatrix(
  columns: string[],
  rows: Record<string, string>[],
): string[][] {
  return rows.map((record) =>
    columns.map((col) => sanitizePlanningCell(record[col] ?? '')),
  );
}

const PRIMARY_PLAN_ROW_LABEL = 'מספר תוכנית עיקרית';

function resolveTakanonPdfUrl(
  extras: ReturnType<typeof parsePlanningExtras>,
): string | undefined {
  const fromMeta = extras.takanonExtraction?.pdf_url?.trim();
  if (fromMeta) return fromMeta.split('#')[0];
  const doc = extras.documents.find(
    (d) => d.type === 'takanon' && d.url && isOfficialTakanonPdfUrl(d.url),
  );
  return doc?.url?.trim().split('#')[0];
}

function mapSection1(
  raw: ApiSection1,
  bundle: ReturnType<typeof getPlanningReportBundle>,
  takanonPdfUrl?: string,
): PlanningSection[] {
  const { fields: f, columns: c, blocks: b } = bundle;
  const fields: PlanningField[] = [];
  if (raw.settlement) fields.push({ label: f.settlement, value: raw.settlement });
  if (raw.gush != null) fields.push({ label: f.gush, value: String(raw.gush) });
  if (raw.helka != null) fields.push({ label: f.helka, value: String(raw.helka) });
  if (raw.area_sqm) fields.push({ label: f.areaSqm, value: raw.area_sqm });
  for (const row of raw.rows ?? []) {
    if (!row.label || !row.value) continue;
    const field: PlanningField = { label: row.label, value: row.value };
    if (takanonPdfUrl && row.label === PRIMARY_PLAN_ROW_LABEL) {
      field.href = takanonPdfUrl;
    }
    fields.push(field);
  }

  const parcels = raw.parcels ?? [];
  const omitParcelTable = singleParcelDuplicatesScalars(
    parcels,
    raw.gush,
    raw.helka,
    raw.area_sqm,
  );
  const parcelColumns = [c.parcelGush, c.parcelHelkaSource, c.parcelAreaSqm];
  const parcelRows = omitParcelTable
    ? []
    : parcels.map((p) => [String(p.gush), String(p.helka), p.area_sqm]);

  const sections: PlanningSection[] = [];
  const main: PlanningSection = {
    title: raw.title,
    fields: fields.length > 0 ? fields : undefined,
  };
  if (parcelRows.length > 0) {
    main.columns = parcelColumns;
    main.rows = parcelRows;
  }
  sections.push(main);

  if (raw.related_plans?.rows?.length) {
    sections.push({
      title: b.relatedPlans,
      columns: raw.related_plans.columns,
      rows: tableRowsToMatrix(raw.related_plans.columns, raw.related_plans.rows),
    });
  }

  if (raw.footnotes?.length) {
    sections.push({
      title: b.areaNotes,
      notes: raw.footnotes,
    });
  }

  return sections;
}

function section2BlockTitle(sectionTitle: string, subtitle?: string): string {
  const st = sanitizePlanningCell(sectionTitle);
  const sub = subtitle?.trim() ? sanitizePlanningCell(subtitle) : '';
  if (!sub || sub === st) return subtitle?.trim() || sectionTitle;
  return sub;
}

function mapSection2(raw: ApiSection2): PlanningSection[] {
  const tables = raw.tables?.length
    ? raw.tables
    : raw.rows?.length
      ? [{ subtitle: undefined, columns: raw.columns ?? [], rows: raw.rows }]
      : [];

  return tables.map((table) => ({
    title: section2BlockTitle(raw.title, table.subtitle),
    columns: table.columns,
    rows: tableRowsToMatrix(table.columns, table.rows),
  }));
}

const TAKANON_SECTION_ORDER = [
  'section_3',
  'section_4',
  'section_5',
  'section_6',
  'section_7',
] as const;

function mapExtractedField(
  field: ApiExtractedField,
  pdfUrl?: string,
): PlanningField {
  return {
    label: field.label_he,
    value: field.value,
    href: pdfUrl && field.page != null ? pdfPageUrl(pdfUrl, field.page) : undefined,
    confidence: field.confidence,
    verified: field.verified,
  };
}

/** §3–§7 blocks from verified תקנון extraction only. */
export function mapExtractedSectionsToPlanningBlocks(
  sections: ApiExtractedSection[],
  pdfUrl: string | undefined,
  _bundle: ReturnType<typeof getPlanningReportBundle>,
): PlanningSection[] {
  const verifiedSections = sections
    .map((section) => ({
      ...section,
      fields: section.fields.filter((f) => f.verified === true),
    }))
    .filter((section) => section.fields.length > 0);

  const sorted = [...verifiedSections].sort(
    (a, b) =>
      TAKANON_SECTION_ORDER.indexOf(
        a.section_key as (typeof TAKANON_SECTION_ORDER)[number],
      ) -
      TAKANON_SECTION_ORDER.indexOf(
        b.section_key as (typeof TAKANON_SECTION_ORDER)[number],
      ),
  );

  const blocks: PlanningSection[] = [];

  for (const section of sorted) {
    blocks.push({
      title: displayTitleForTakanonSection(
        section.section_key,
        section.title,
      ),
      fields: section.fields.map((f) => mapExtractedField(f, pdfUrl)),
    });
  }

  return blocks;
}

function mapSection3(
  raw: ApiSection3,
  bundle: ReturnType<typeof getPlanningReportBundle>,
  section1Facts: Set<string>,
): PlanningSection[] {
  const { blocks: b } = bundle;
  const out: PlanningSection[] = [];

  const lotSummary = (raw.lot_summary ?? []).filter(
    (row) => row.label && row.value && !isDuplicateFact(row.label, row.value, section1Facts),
  );
  if (lotSummary.length > 0) {
    for (const row of lotSummary) {
      rememberFact(row.label, row.value, section1Facts);
    }
    out.push({
      title: b.lotSummary,
      fields: lotSummary,
    });
  }

  if (raw.table_a?.rows?.length) {
    out.push({
      title: b.tableA,
      columns: raw.table_a.columns,
      rows: tableRowsToMatrix(raw.table_a.columns, raw.table_a.rows),
    });
  }

  if (raw.table_b?.rows?.length) {
    out.push({
      title: b.tableB,
      columns: raw.table_b.columns,
      rows: tableRowsToMatrix(raw.table_b.columns, raw.table_b.rows),
    });
  }

  for (const group of raw.topic_groups ?? []) {
    const items = group.items
      .filter((item) => item.detail?.trim() && !isDuplicateFact(item.topic, item.detail, section1Facts))
      .map((item) => ({
        label: item.plan_ref ? `${item.topic} (${item.plan_ref})` : item.topic,
        value: item.detail,
      }));
    if (items.length === 0) continue;
    out.push({
      title: group.category,
      items,
    });
  }

  const flatTopics = [...(raw.topics ?? []), ...(raw.rows ?? [])].filter(
    (t) => t.label && t.value,
  );
  if (flatTopics.length > 0 && !(raw.topic_groups?.length)) {
    out.push({ title: raw.title, items: flatTopics });
  }

  return out.length > 0 ? out : [{ title: raw.title }];
}

function mapSection4(raw: ApiSection4, bundle: ReturnType<typeof getPlanningReportBundle>): PlanningSection {
  const { blocks: b } = bundle;
  const notes = [...(raw.notes ?? [])];
  if (raw.disclaimer && !notes.includes(raw.disclaimer)) {
    notes.unshift(raw.disclaimer);
  }

  const fields: PlanningField[] = [];
  const editor = raw.editor_contact;
  if (editor) {
    if (editor.role) fields.push({ label: b.editorRole, value: editor.role });
    if (editor.name) fields.push({ label: b.editorName, value: editor.name });
    if (editor.phone) fields.push({ label: b.phone, value: editor.phone });
    if (editor.fax) fields.push({ label: b.fax, value: editor.fax });
    if (editor.address) fields.push({ label: b.address, value: editor.address });
  }
  const approver = raw.approver_contact;
  if (approver?.name) fields.push({ label: b.approver, value: approver.name });
  if (approver?.phone) fields.push({ label: b.approverPhone, value: approver.phone });

  return {
    title: raw.title,
    notes: notes.length > 0 ? notes : undefined,
    fields: fields.length > 0 ? fields : undefined,
  };
}

export type NormalizedPlanningView = {
  reportMeta?: PlanningReportMetaUi;
  section1Title: string;
  section1Blocks: PlanningSection[];
  section2Title: string;
  section2Blocks: PlanningSection[];
  section3Title: string;
  section3Blocks: PlanningSection[];
  section4Title: string;
  section4?: PlanningSection;
};

/** Maps backend `section_*` JSON to UI sections. */
export function normalizePlanningView(
  sectionsJson: PlanningSectionsJson | ApiPlanningSections,
  locale: AppLanguage = 'he',
): NormalizedPlanningView {
  const bundle = getPlanningReportBundle(locale);
  const { sectionTitles: st } = bundle;

  if ('section1' in sectionsJson || 'section2' in sectionsJson) {
    const legacy = sectionsJson as PlanningSectionsJson;
    const view: NormalizedPlanningView = {
      section1Title: legacy.section1?.title ?? bundle.section1Default,
      section1Blocks: legacy.section1 ? [legacy.section1] : [],
      section2Title: legacy.section2?.title ?? bundle.section2Default,
      section2Blocks: legacy.section2 ? [legacy.section2] : [],
      section3Title: legacy.section3?.title ?? bundle.section3Default,
      section3Blocks: legacy.section3 ? [legacy.section3] : [],
      section4Title: legacy.section4?.title ?? bundle.section4Default,
      section4: legacy.section4,
    };
    return localizePlanningView(view, locale);
  }

  const api = sectionsJson as ApiPlanningSections;
  const extras = parsePlanningExtras(api);
  const pdfUrl = resolveTakanonPdfUrl(extras);
  const section1Facts = api.section_1
    ? collectSection1FactKeys(api.section_1, bundle.fields)
    : new Set<string>();
  const baseSection3 = api.section_3
    ? mapSection3(api.section_3, bundle, section1Facts)
    : [];
  const fromExtractedSections = mapExtractedSectionsToPlanningBlocks(
    extras.extractedSections,
    pdfUrl,
    bundle,
  );

  const view: NormalizedPlanningView = {
    reportMeta: api.report_meta,
    section1Title: api.section_1?.title ?? st.landDetails,
    section1Blocks: api.section_1 ? mapSection1(api.section_1, bundle, pdfUrl) : [],
    section2Title: api.section_2?.title ?? st.relatedPlans,
    section2Blocks: api.section_2 ? mapSection2(api.section_2) : [],
    section3Title: api.section_3?.title ?? st.rights,
    section3Blocks: [
      ...baseSection3,
      ...fromExtractedSections,
    ],
    section4Title: api.section_4?.title ?? st.notes,
    section4: api.section_4 ? mapSection4(api.section_4, bundle) : undefined,
  };
  return localizePlanningView(view, locale);
}

/** @deprecated Use normalizePlanningView */
export function normalizePlanningSections(
  sectionsJson: PlanningSectionsJson | ApiPlanningSections,
): PlanningSectionsJson {
  const view = normalizePlanningView(sectionsJson);
  return {
    section1: view.section1Blocks[0],
    section2: view.section2Blocks[0],
    section3: view.section3Blocks[0],
    section4: view.section4,
  };
}

export function formatPlanningSource(
  source: string | undefined,
  locale: AppLanguage = 'he',
): string | undefined {
  if (!source) return undefined;
  const sources = getPlanningReportBundle(locale).sources;
  const key = source as keyof typeof sources;
  if (key in sources) return sources[key];
  return source;
}

/** National XPLAN viewer (מינהל התכנון). */
export function xplanOfficialUrl(): string {
  return 'https://ags.iplan.gov.il/xplan/';
}

/** @deprecated Committee VGM link — not used when source is XPLAN */
export function vgmOfficialUrl(
  gush?: number,
  helka?: number,
): string | undefined {
  if (gush == null || helka == null) return undefined;
  return `https://www.vgm.co.il/gush2/?gush=${gush}&helka=${helka}`;
}
